<template>
  <div class="pa-10">
    <title-page icon="mdi-domain">{{ $t('organization.perimeter.search') }}</title-page>

    <v-row v-if="currentUser.permissions.includes('ORGANIZATION_WRITE')">
      <v-col class="text-right">
        <v-btn class="mr-2" :to="{name: 'organization-sub-perimeter'}" small>
          <v-icon left>mdi-shape</v-icon>
          {{ $t('organization.sub-perimeter.search') }}
        </v-btn>
        <v-btn :to="{name: 'organization-perimeter.add'}" small class="ml-3">
          <v-icon left>mdi-plus</v-icon>
          {{ $t('organization.perimeter.add') }}
        </v-btn>
      </v-col>
    </v-row>

    <v-row>
      <v-col>
        <search-field :placeholder="$t('search')" :search.sync="search" clearable />
      </v-col>
    </v-row>

    <v-row class="mt-6">
      <v-col>
        <v-data-table
          :headers="headers"
          :items="items"
          :loading="loading"
          :options.sync="options"
          :server-items-length="total"
        >
          <template #item.name="{value}">
            {{ localizedString(value) }}
          </template>
          <template #item.subCount="{item}">
            <v-tooltip bottom>
              <template v-slot:activator="{ on, attrs }">
                <v-btn text small v-on="on" v-bind="attrs" @click="openSubPerimetersModal(item)">
                  {{ item.subCount ?? 0 }}
                </v-btn>
              </template>
              <span>{{ $t('organization.sub-perimeter.search') }}</span>
            </v-tooltip>
          </template>
          <template #item.options="{item}">
            <v-btn icon :to="{name: 'organization-perimeter.edit', params: {id: item.id}}"><v-icon>mdi-pencil</v-icon></v-btn>
          </template>
        </v-data-table>
      </v-col>
    </v-row>

    <v-dialog max-width="800" v-model="subPerimeterListModal" scrollable>
      <v-card>
        <v-card-title class="d-flex align-center justify-space-between">
          <span>{{ $t('organization.sub-perimeter.search') }}</span>
          <v-autocomplete
            class="ml-4"
            type="select"
            :label="$t('add')"
            :items="subAutocompleteItems"
            v-model="subSelectedToAdd"
            clearable
            hide-selected
            dense
            :loading="subAutocompleteLoading"
            :search-input.sync="subAutocompleteSearch"
            @change="onSelectSubToAdd"
          />
        </v-card-title>
        <v-divider class="mb-6"></v-divider>
        <v-card-text>
          <v-data-table
            :headers="subHeaders"
            :items="subItems"
            :loading="subLoading"
            :options.sync="subOptions"
            :server-items-length="subTotal"
            dense
          >
            <template #item.name="{value}">
              {{ localizedString(value) }}
            </template>
            <template #item.options="{item}">
              <v-btn icon @click="unlinkSubFromSelectedPerimeter(item)"><v-icon>mdi-link-off</v-icon></v-btn>
            </template>
          </v-data-table>
        </v-card-text>
        <v-divider></v-divider>
        <v-card-actions>
          <v-row class="ma-0">
            <v-col class="text-right">
              <v-btn small @click="subPerimeterListModal = false">{{ $t('close') }}</v-btn>
            </v-col>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import OrganizationPerimeterRepository from "@repository/OrganizationPerimeterRepository";

export default {
  data() {
    return {
      loading: false,
      search: null,
      items: [],
      total: 0,
      options: {page:1, itemsPerPage:10, sortBy:[], sortDesc:[]},
      // modal state
      subPerimeterListModal: false,
      subLoading: false,
      subItems: [],
      subTotal: 0,
      subOptions: { page: 1, itemsPerPage: 10, sortBy: ['name'], sortDesc: [false] },
      selectedPerimeter: null,
      // autocomplete for adding sub-perimeters
      subAutocompleteItems: [],
      subAutocompleteSearch: null,
      subAutocompleteLoading: false,
      subSelectedToAdd: null,
    }
  },
  computed: {
    headers() {
      return [
        {text: this.$t('code'), value: 'code'},
        {text: this.$t('name'), value: 'name'},
        {text: this.$t('count'), value: 'subCount', width: 120},
        {text: '', value: 'options', width: 140},
      ]
    },
    subHeaders() {
      return [
        {text: this.$t('code'), value: 'code'},
        {text: this.$t('name'), value: 'name'},
        {text: '', value: 'options', width: 120},
      ]
    }
  },
  watch: {
    search() { this.triggerSearch() },
    options: { deep: true, handler() { this.searchData() } },
    subOptions: { deep: true, handler() { this.loadSubPerimeters() } },
    subAutocompleteSearch(val) {
      this.fetchSubAutocomplete(val)
    }
  },
  mounted() {
    if(!this.currentUser.permissions.includes('ORGANIZATION_WRITE')) {
      this.$router.replace({name: 'dashboard'})
      return
    }
  },
  methods: {
    fetchSubAutocomplete(searchText) {
      if(!this.selectedPerimeter) return
      this.subAutocompleteLoading = true
      const offset = 0, limit = 10
      // Exclude already linked sub-perimeters
      OrganizationPerimeterRepository.searchSubPerimeters(searchText, offset, limit, 'name', 'asc', { excludePerimeterIds: [this.selectedPerimeter.id] })
        .then(res => {
          this.subAutocompleteItems = (res.items || []).map(i => ({value: i.id, text: this.localizedString(i.name)}))
          this.subAutocompleteLoading = false
        }).catch(() => { this.subAutocompleteLoading = false })
    },
    onSelectSubToAdd(subId) {
      if(!subId || !this.selectedPerimeter) return
      this.$dialog.confirm({text: this.$t('are-you-sure')}).then(result => {
        if(!result) { this.subSelectedToAdd = null; return }
        this.showLoading(true)
        // use dedicated link route
        OrganizationPerimeterRepository.linkSubPerimeterToPerimeter(subId, this.selectedPerimeter.id).then(() => {
          this.$dialog.notify.success(this.$t('update-success'))
          this.subSelectedToAdd = null
          this.subAutocompleteSearch = ''
          // refresh modal list, main list count, and autocomplete options
          this.loadSubPerimeters()
          this.searchData() // refresh counts
          this.fetchSubAutocomplete(this.subAutocompleteSearch)
          this.showLoading(false)
        }).catch(err => {
          this.showLoading(false)
          this.notifyError(err)
        })
      })
    },
    unlinkSubFromSelectedPerimeter(item) {
      if(!this.selectedPerimeter) return
      this.$dialog.confirm({text: this.$t('are-you-sure')}).then(ok => {
        if(!ok) return
        this.showLoading(true)
        OrganizationPerimeterRepository.unlinkSubPerimeterFromPerimeter(item.id, this.selectedPerimeter.id).then(() => {
          this.$dialog.notify.success(this.$t('update-success'))
          // refresh modal list, counts and autocomplete options
          this.loadSubPerimeters()
          this.searchData()
          this.fetchSubAutocomplete(this.subAutocompleteSearch)
          this.showLoading(false)
        }).catch(err => {
          this.showLoading(false)
          this.notifyError(err)
        })
      })
    },
    openSubPerimetersModal(item) {
      this.selectedPerimeter = item
      this.subPerimeterListModal = true
      // reset options page
      this.subOptions.page = 1
      this.loadSubPerimeters()
      // prime autocomplete list
      this.subAutocompleteSearch = ''
      this.fetchSubAutocomplete('')
    },
    loadSubPerimeters() {
      if(!this.selectedPerimeter) return
      this.subLoading = true
      const q = null
      const offset = this.subOptions.itemsPerPage * this.subOptions.page - this.subOptions.itemsPerPage
      const limit = this.subOptions.itemsPerPage
      const orderBy = this.subOptions.sortBy.length > 0 ? this.subOptions.sortBy[0] : 'name'
      const orderDirection = this.subOptions.sortDesc.length > 0 && this.subOptions.sortDesc[0] ? 'desc' : 'asc'
      OrganizationPerimeterRepository.searchSubPerimeters(q, offset, limit, orderBy, orderDirection, { perimeterIds: [this.selectedPerimeter.id] })
        .then(result => {
          this.subTotal = result.total
          this.subItems = (result.items || [])
          this.subLoading = false
        }).catch(err => {
          this.notifyError(err)
          this.subLoading = false
        })
    },
    triggerSearch() {
      if(this.options.page > 1) { this.options.page = 1 } else { this.searchData() }
    },
    searchData() {
      if(!this.options) return
      this.loading = true
      const query = this.search
      const offset = this.options.itemsPerPage * this.options.page - this.options.itemsPerPage
      const limit = this.options.itemsPerPage
      const orderBy = this.options.sortBy.length > 0 ? this.options.sortBy[0] : 'name'
      const orderDirection = this.options.sortDesc.length > 0 && this.options.sortDesc[0] ? 'desc' : 'asc'

      OrganizationPerimeterRepository.searchPerimeters(query, offset, limit, orderBy, orderDirection)
        .then(async result => {
          this.total = result.total
          let perimeters = (result.items || [])

          // Option A: if API supports counting on the server, expect 'subCount' in items.
          // Option B: compute counts quickly by fetching counts per perimeter (limit: 1 with perimeter filter and using total).
          const promises = perimeters.map(p =>
            OrganizationPerimeterRepository.searchSubPerimeters(null, 0, 1, 'name', 'asc', { perimeterIds: [p.id] })
              .then(r => ({ id: p.id, count: r.total || 0 }))
              .catch(() => ({ id: p.id, count: 0 }))
          )
          const counts = await Promise.all(promises)
          const countMap = counts.reduce((acc, c) => { acc[c.id] = c.count; return acc }, {})

          this.items = perimeters.map(it => ({
            ...it,
            subCount: typeof it.subCount === 'number' ? it.subCount : (countMap[it.id] || 0),
          }))
          this.loading = false
        }).catch(err => {
          this.notifyError(err)
          this.loading = false
        })
    }
  }
}
</script>
